import { Scanner } from '@yudiel/react-qr-scanner';
import React, { useState, useEffect, useMemo, useCallback, useDeferredValue } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { 
  getFirestore, collection, addDoc, query, where, getDocs, 
  orderBy, Timestamp, updateDoc, doc, limit, setDoc, arrayUnion, increment 
} from 'firebase/firestore';
import { Modal, Form, Row, Col, InputGroup, Spinner } from "react-bootstrap";
import { toast } from 'react-toastify';
import {
  FaTimesCircle, FaCheckCircle, FaBan, FaFilter,
  FaClipboardList, FaExclamationTriangle, FaShieldAlt, FaDownload, FaChevronDown, FaInfoCircle, FaUserCheck
} from 'react-icons/fa';

import LogCardItem from "./LogCardItem";
import EventListManager from "./EventListManager";
import './xDocList.css';

const ADMIN_PASSCODE = process.env.REACT_APP_ADMIN_PASSCODE || "1234";
const INITIAL_DISPLAY_LIMIT = 20;

const formatScanTime = (val) => {
  if (!val) return '--';
  if (typeof val.toDate === 'function') return val.toDate().toLocaleString();
  if (val instanceof Date) return val.toLocaleString();
  return new Date(val).toLocaleString();
};

const formatScanTimeOnlyHelper = (val) => {
  if (!val) return '--';
  const d = typeof val.toDate === 'function' ? val.toDate() : (val instanceof Date ? val : new Date(val));
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const CameraScanner = React.memo(({ onScan, isPaused }) => {
  return (
    <Scanner
      onScan={onScan}
      paused={isPaused}
      formats={['qr_code', 'pdf417']}
      styles={{ container: { height: '100%', width: '100%' }, video: { objectFit: 'cover' } }}
    />
  );
});

const QRWebAndNativeScan = () => {
  const [scannedResult, setScannedResult] = useState('');
  const [isPaused, setIsPaused] = useState(false);

  const [isAdmin, setIsAdmin] = useState(false);
  const [isBarAdmin, setIsBarAdmin] = useState(false);

  // Modals
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [showEventListModal, setShowEventListModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const [banForm, setBanForm] = useState({ firstName: '', lastName: '', sponsor: '', reason: '', date: '' });
  const [savingBan, setSavingBan] = useState(false);
  const [pendingGuestLog, setPendingGuestLog] = useState(null);
  const [pendingBanReason, setPendingBanReason] = useState('');
  const [overridePasscode, setOverridePasscode] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [isDifferentPerson, setIsDifferentPerson] = useState(false);

  const [activeLogDocId, setActiveLogDocId] = useState(null);

  const [modalContent, setModalContent] = useState({ 
    title: '', name: '', time: '', type: '', variant: 'success', 
    subtext: '', isFlagged: false, hasIncident: false, isBanned: false, email: '' 
  });
  const [selectedLog, setSelectedLog] = useState(null);

  const [guestNameInput, setGuestNameInput] = useState('');
  const [lastMemberName, setLastMemberName] = useState('');
  const [lastMemberEmail, setLastMemberEmail] = useState('');
  const [guestItems, setGuestItems] = useState([]);

  const [quickViewMode, setQuickViewMode] = useState('all'); 
  const [isShiftView, setIsShiftView] = useState(false); // <--- Added shift view state

  const [recentLogs, setRecentLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const [visibleCount, setVisibleCount] = useState(INITIAL_DISPLAY_LIMIT);

  const [loadingLogs, setLoadingLogs] = useState(false);
  const [exportingCSV, setExportingCSV] = useState(false);

  const [manualMode, setManualMode] = useState(false);
  const [isAdminAuth, setIsAdminAuth] = useState(true);

  const todayStr = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);

  const [passcodeAttempt, setPasscodeAttempt] = useState('');
  const [manualInput, setManualInput] = useState('');

  const db = getFirestore();

  const isAnyModalOpen = showModal || showDetailModal || showGuestModal || showBanModal || showOverrideModal || showEventListModal || showHistoryModal;
  const effectiveIsPaused = isPaused || isAnyModalOpen;

  const formatDisplayValue = useCallback((str) => {
    if (!str) return "";
    if (str.includes('@')) return str.toLowerCase();
    return str.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }, []);

  const formatScanTimeOnly = useCallback((val) => formatScanTimeOnlyHelper(val), []);

  const getActiveShiftDateStr = (date = new Date()) => {
    const d = new Date(date);
    if (d.getHours() < 2) {
      d.setDate(d.getDate() - 1);
    }
    return d.toISOString().split('T')[0];
  };

  useEffect(() => {
    let isMounted = true;
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && user.email) {
        try {
          const q = query(collection(db, "profiles"), where("email", "==", user.email.toLowerCase()));
          const snap = await getDocs(q);
          if (isMounted && !snap.empty) {
            const profileData = snap.docs[0].data();
            setIsAdmin(!!profileData.isAdmin);
            setIsBarAdmin(!!profileData.isBarAdmin);
          }
        } catch (err) {
          console.error("Error fetching user profile permissions:", err);
        }
      }
    });
    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [db]);

  const getLogsCollectionName = (date = new Date()) => {
    const year = date instanceof Date ? date.getFullYear() : new Date().getFullYear();
    return `access_logs_${year}`;
  };

  const getStaffVerifierTag = useCallback((suffix = "") => {
    const staffName = lastMemberName ? formatDisplayValue(lastMemberName) : "Admin Station";
    const baseTag = `${staffName} (Admin)`;
    return suffix ? `${baseTag} - ${suffix}` : baseTag;
  }, [lastMemberName, formatDisplayValue]);

  const fetchLogs = useCallback(async (isTodayOnly = true, isShift = false) => {
    setLoadingLogs(true);
    try {
      let q;
      const now = new Date();
      const logsRef = collection(db, getLogsCollectionName(isTodayOnly ? now : new Date(startDate)));

      if (isShift) {
        // SHIFT MODE: Look back to 4:00 PM yesterday to show all members from the active shift
        const shiftStart = new Date(now);
        if (now.getHours() < 4) {
          shiftStart.setDate(shiftStart.getDate() - 1);
        }
        shiftStart.setHours(16, 0, 0, 0); // 4:00 PM

        q = query(
          logsRef, 
          where("lastScannedAt", ">=", Timestamp.fromDate(shiftStart)), 
          orderBy("lastScannedAt", "desc"), 
          limit(250)
        );
      } else if (isTodayOnly) {
        // STANDARD MODE: Midnight to now
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        q = query(
          logsRef, 
          where("lastScannedAt", ">=", Timestamp.fromDate(startOfToday)), 
          orderBy("lastScannedAt", "desc"), 
          limit(250)
        );
      } else {
        // HISTORICAL RANGE MODE
        const start = new Date(startDate + "T00:00:00");
        const end = new Date(endDate + "T23:59:59");
        q = query(
          logsRef, 
          where("lastScannedAt", ">=", Timestamp.fromDate(start)), 
          where("lastScannedAt", "<=", Timestamp.fromDate(end)), 
          orderBy("lastScannedAt", "desc"), 
          limit(500)
        );
      }

      const querySnapshot = await getDocs(q);
      const baseLogs = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          scannedAt: data.lastScannedAt || data.scannedAt,
          guestCount: data.guestCount || 0,
          visitCount: data.visitCount || (data.scans ? data.scans.length : 1)
        };
      });

      setRecentLogs(baseLogs);
      setVisibleCount(INITIAL_DISPLAY_LIMIT);
    } catch (err) {
      console.error("Main fetchLogs error:", err);
      toast.error("Error loading access logs");
    } finally {
      setLoadingLogs(false);
    }
  }, [db, startDate, endDate]);

  const fetchGuestsForMember = useCallback(async (email) => {
    if (!email) return;
    const logsRef = collection(db, getLogsCollectionName());
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const q = query(logsRef, where("signedInBy", "==", email.toLowerCase()), where("entryType", "==", "Guest"), where("lastScannedAt", ">=", Timestamp.fromDate(startOfToday)), orderBy("lastScannedAt", "desc"), limit(50));
    const snap = await getDocs(q);
    setGuestItems(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(g => g.status !== 'cancelled' && g.status !== 'removed'));
  }, [db]);

  useEffect(() => { 
    if (isAdminAuth) fetchLogs(true, isShiftView); 
  }, [isAdminAuth, fetchLogs, isShiftView]);

  const logVisit = useCallback(async (name, method, type, raw = "", sponsor = "", expired = false, flagged = false, incident = false, isBanned = false, bypassReason = "", eventListName = "") => {
    const timestamp = new Date();
    try {
      const hasViolation = expired || flagged || incident || isBanned;
      const isBypassed = !!bypassReason;
      const autoVerified = !hasViolation || isBypassed; 
      const verifierMethod = isBypassed ? `Bypass: ${bypassReason.trim()}` : `${method} Sign-In`;

      const shiftDateStr = getActiveShiftDateStr(timestamp);
      const cleanName = name.toLowerCase().trim().replace(/\s+/g, '_');
      const userKey = type === 'Guest' ? `guest_${cleanName}` : ((raw && raw.includes('@')) ? raw.toLowerCase().trim() : cleanName);
      const docId = `${shiftDateStr}_${userKey}`;

      const executorTag = getStaffVerifierTag(verifierMethod);
      
      // Full Telemetry Event Record for scans Array
      const eventRecord = { 
        timestamp: Timestamp.fromDate(timestamp), 
        method, 
        type, 
        status: isBypassed ? 'bypassed' : (hasViolation ? 'denied' : 'active'),
        isVerified: autoVerified,
        isBypassed, 
        bypassReason: bypassReason || null, 
        isExpired: !!expired,
        isFlagged: !!flagged,
        hasIncident: !!incident,
        isBanned: !!isBanned,
        verifier: executorTag, 
        eventListName: eventListName || null 
      };
      
      const logDocRef = doc(db, getLogsCollectionName(timestamp), docId);

      const payload = {
        fullName: name, 
        status: isBypassed ? 'bypassed' : (hasViolation && !isBypassed ? 'denied' : 'active'), 
        entryType: type, 
        method, 
        rawPayload: raw, 
        signedInBy: raw && raw.includes('@') ? raw.toLowerCase() : raw,
        sponsorName: sponsor || raw,
        eventListName: eventListName || null,
        isExpired: !!expired, 
        isFlagged: !!flagged, 
        hasIncident: !!incident, 
        isBanned: !!isBanned, 
        bypassReason: bypassReason || null,
        lastScannedAt: Timestamp.fromDate(timestamp), 
        scannedAt: Timestamp.fromDate(timestamp), 
        isVerified: autoVerified, 
        verifiedBy: executorTag,
        visitCount: increment(1), 
        scans: arrayUnion(eventRecord)
      };

      await setDoc(logDocRef, payload, { merge: true });
      setActiveLogDocId(docId);
      fetchLogs(true, isShiftView);
      return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (err) { 
      console.error("Error in logVisit:", err);
      return new Date().toLocaleTimeString(); 
    }
  }, [db, getStaffVerifierTag, fetchLogs, isShiftView]);

  const verifyMemberProfile = useCallback(async (identifier) => {
    const cleaned = identifier.trim().toLowerCase();
    const isEmail = /\S+@\S+\.\S+/.test(cleaned);
    const q = query(collection(db, "profiles"), where(isEmail ? 'email' : 'name', "==", isEmail ? cleaned : identifier.trim()), limit(1));
    const snap = await getDocs(q);
    let memberData = null;

    if (!snap.empty) {
      const data = snap.docs[0].data();
      if (data.isMember === true || data.member === true) {
        let isExp = false; 
        let dateS = "N/A"; 
        let isFlagged = false;
        if (data.expirationDate) {
          const expD = data.expirationDate.toDate ? data.expirationDate.toDate() : new Date(data.expirationDate);
          dateS = expD.toLocaleDateString();
          if (expD < new Date()) isExp = true;
        }
        memberData = { name: data.name, expired: isExp, expiryDate: dateS, email: data.email || data.rawPayload || "", flagged: isFlagged, incident: data.hasIncident === true, isMember: true };
      }
    }

    const searchName = memberData ? memberData.name : identifier.trim();
    const names = searchName.split(' ');
    const banQ = query(collection(db, "banned_list"), where("firstName", "==", names[0] || ''), where("lastName", "==", names.slice(1).join(' ') || ''), limit(1));
    const banSnap = await getDocs(banQ);

    if (!banSnap.empty) {
      return { ...(memberData || { name: searchName, email: identifier }), isBanned: true, banReason: banSnap.docs[0].data().reason };
    }
    return memberData;
  }, [db]);

  const handleVerifyEntry = useCallback(async (logEntry) => {
    try {
      const logRef = doc(db, getLogsCollectionName(), logEntry.id);
      const checkResult = await verifyMemberProfile(logEntry.fullName);

      if (checkResult && checkResult.isBanned) {
        setPendingGuestLog(logEntry); setPendingBanReason(checkResult.banReason || 'No reason specified');
        setShowOverrideModal(true); return;
      }

      const verifierTag = getStaffVerifierTag();
      const updateEvent = {
        timestamp: Timestamp.now(),
        method: "Manual Verification",
        type: logEntry.entryType || "Member",
        status: "active",
        isVerified: true,
        isBypassed: false,
        bypassReason: null,
        verifier: verifierTag
      };

      await updateDoc(logRef, { 
        isVerified: true, 
        verifiedBy: verifierTag, 
        lastScannedAt: Timestamp.now(),
        scans: arrayUnion(updateEvent)
      });
      toast.success(`${formatDisplayValue(logEntry.fullName)} verified!`);
      fetchLogs(true, isShiftView);
    } catch (err) { toast.error("Failed to verify individual"); }
  }, [db, verifyMemberProfile, getStaffVerifierTag, formatDisplayValue, fetchLogs, isShiftView]);

  const handleNoShow = useCallback(async (guestLogId) => {
    try {
      const logRef = doc(db, getLogsCollectionName(), guestLogId);
      const updateEvent = {
        timestamp: Timestamp.now(),
        method: "Marked No Show",
        type: "Guest",
        status: "removed",
        isVerified: false,
        isBypassed: false,
        verifier: getStaffVerifierTag("No Show")
      };

      await updateDoc(logRef, { 
        remove: true, 
        noshow: true, 
        status: 'removed', 
        verifiedBy: getStaffVerifierTag("No Show"), 
        removedAt: Timestamp.now(),
        scans: arrayUnion(updateEvent)
      });
      toast.info("Guest marked as No Show");
      fetchLogs(true, isShiftView);
    } catch (err) { toast.error("Failed to mark guest as No Show"); }
  }, [db, getStaffVerifierTag, fetchLogs, isShiftView]);

  const handleOpenBanModal = useCallback((log) => {
    const names = (log.fullName || '').trim().split(' ');
    setBanForm({ firstName: names[0] || '', lastName: names.slice(1).join(' ') || '', sponsor: formatDisplayValue(log.sponsorName || log.signedInBy || ''), reason: `Banned via Scanner`, date: formatScanTime(log.lastScannedAt || log.scannedAt) });
    setShowBanModal(true);
  }, [formatDisplayValue]);

  const downloadCSVByDate = async () => {
    setExportingCSV(true);
    try {
      const start = new Date(startDate + "T00:00:00");
      const end = new Date(endDate + "T23:59:59");
      const logsRef = collection(db, getLogsCollectionName(start));
      const q = query(logsRef, where("lastScannedAt", ">=", Timestamp.fromDate(start)), where("lastScannedAt", "<=", Timestamp.fromDate(end)), orderBy("lastScannedAt", "desc"), limit(1000));
      const snap = await getDocs(q);
      const exportLogs = snap.docs.map(d => d.data());

      if (exportLogs.length === 0) {
        toast.info("No access records found for selected range.");
        return;
      }

      const headers = ["Full Name", "Entry Type", "Method", "Sponsor", "Verified By", "Last Scanned Time"];
      const rows = exportLogs.map(log => [
        `"${log.fullName || ''}"`,
        `"${log.entryType || 'Member'}"`,
        `"${log.method || ''}"`,
        `"${log.sponsorName || log.signedInBy || ''}"`,
        `"${log.verifiedBy || ''}"`,
        `"${formatScanTime(log.lastScannedAt || log.scannedAt)}"`
      ].join(","));

      const blob = new Blob([[headers.join(","), ...rows].join("\n")], { type: 'text/csv;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Access_Logs_${startDate}_to_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Exported ${exportLogs.length} records.`);
    } catch (err) {
      toast.error("Failed to export CSV");
    } finally {
      setExportingCSV(false);
    }
  };

  const handleBypass = async () => {
    const code = window.prompt("SECURITY OVERRIDE: Enter Admin Passcode to continue:");
    if (code !== ADMIN_PASSCODE) return toast.error("Unauthorized: Invalid Passcode");

    const reason = window.prompt("Passcode Accepted. Enter reason for bypass (REQUIRED):");
    if (!reason || reason.trim().length < 3) return toast.warn("Bypass cancelled: Reason is required.");

    try {
      const verifierTag = getStaffVerifierTag(`Bypass: ${reason.trim()}`);
      const bypassEvent = {
        timestamp: Timestamp.now(),
        method: "Manual Bypass",
        type: modalContent.type || "Member",
        status: "bypassed",
        isVerified: true,
        isBypassed: true,
        bypassReason: reason.trim(),
        verifier: verifierTag
      };

      if (activeLogDocId) {
        const existingDocRef = doc(db, getLogsCollectionName(), activeLogDocId);
        await updateDoc(existingDocRef, {
          status: 'bypassed',
          bypassReason: reason.trim(),
          isVerified: true,
          verifiedBy: verifierTag,
          scans: arrayUnion(bypassEvent)
        });
        fetchLogs(true, isShiftView);
      } else {
        await logVisit(modalContent.name, "Manual Bypass", modalContent.type, modalContent.email || "", lastMemberName || lastMemberEmail || "Admin", false, false, false, false, reason);
      }
    } catch (err) {
      console.error("Error updating document on bypass:", err);
      await logVisit(modalContent.name, "Manual Bypass", modalContent.type, modalContent.email || "", lastMemberName || lastMemberEmail || "Admin", false, false, false, false, reason);
    }

    toast.success("Entry Bypassed & Verified");
    setShowModal(false);
    setScannedResult('');
    setIsPaused(false);
  };

  const handleSaveBan = async () => {
    if (!banForm.firstName.trim() || !banForm.lastName.trim()) {
      return toast.error("First Name and Last Name are required");
    }

    setSavingBan(true);
    try {
      await addDoc(collection(db, "banned_list"), {
        firstName: banForm.firstName.trim(),
        lastName: banForm.lastName.trim(),
        sponsor: banForm.sponsor.trim() || null,
        reason: banForm.reason.trim() || "No reason provided",
        addedAt: Timestamp.now()
      });

      toast.error("Individual added to Global Ban List");
      setShowBanModal(false);
      fetchLogs(true, isShiftView);
    } catch (err) {
      console.error("Error creating banned document:", err);
      toast.error("System error: Could not save ban entry");
    } finally {
      setSavingBan(false);
    }
  };

  const handleConfirmOverride = async () => {
    if (!pendingGuestLog) return;

    if (!isDifferentPerson) {
      const logRef = doc(db, getLogsCollectionName(), pendingGuestLog.id);
      const updateEvent = {
        timestamp: Timestamp.now(),
        method: "Ban Blocked",
        type: pendingGuestLog.entryType || "Guest",
        status: "blocked",
        isBanned: true,
        verifier: getStaffVerifierTag("Ban Confirmed")
      };
      await updateDoc(logRef, { 
        status: 'blocked', 
        isBanned: true, 
        banReason: pendingBanReason,
        scans: arrayUnion(updateEvent)
      });
      setShowOverrideModal(false);
      toast.error("Verification cancelled: Individual is banned.");
      fetchLogs(true, isShiftView);
      return;
    }

    if (overridePasscode !== ADMIN_PASSCODE) {
      return toast.error("Unauthorized: Invalid Admin Passcode.");
    }

    if (!overrideReason.trim() || overrideReason.trim().length < 3) {
      return toast.warn("Override reason is required.");
    }

    try {
      const logRef = doc(db, getLogsCollectionName(), pendingGuestLog.id);
      const verifierTag = getStaffVerifierTag(`Name Override: ${overrideReason.trim()}`);
      const overrideEvent = {
        timestamp: Timestamp.now(),
        method: "Name Match Override",
        type: pendingGuestLog.entryType || "Guest",
        status: "bypassed",
        isVerified: true,
        isBypassed: true,
        bypassReason: overrideReason.trim(),
        verifier: verifierTag
      };

      await updateDoc(logRef, {
        isVerified: true,
        status: 'bypassed',
        isBanned: false,
        bypassReason: overrideReason.trim(),
        verifiedBy: verifierTag,
        lastScannedAt: Timestamp.now(),
        scans: arrayUnion(overrideEvent)
      });

      toast.success(`${formatDisplayValue(pendingGuestLog.fullName)} verified via Admin Override.`);
      setShowOverrideModal(false);
      setOverridePasscode('');
      setOverrideReason('');
      fetchLogs(true, isShiftView);
    } catch (err) {
      console.error("Error committing override:", err);
      toast.error("Failed to process override.");
    }
  };

  const handleQuickGuestAdd = async () => {
    const guestName = guestNameInput.trim();
    if (!guestName) return toast.error("Enter guest name");

    if (!lastMemberEmail || lastMemberEmail.trim() === "") {
      return toast.error("Guest addition failed: Missing sponsor synchronization link.");
    }

    try {
      const guestCheck = await verifyMemberProfile(guestName);
      if (guestCheck && guestCheck.isBanned) {
        await logVisit(guestName, "Attempt", "Guest", lastMemberEmail, lastMemberName || lastMemberEmail, false, false, false, true, "");
        toast.error(`${guestName} is on the Global Ban List.`);
        return;
      }
      await logVisit(guestName, "Quick Add", "Guest", lastMemberEmail, lastMemberName || lastMemberEmail);
      toast.success(`Guest added: ${formatDisplayValue(guestName)}`);
      setGuestNameInput('');
      if (lastMemberEmail) fetchGuestsForMember(lastMemberEmail);
    } catch (err) {
      console.error("Error adding quick guest:", err);
    }
  };

  const handleRemoveGuest = async (guestLogId) => {
    try {
      const logRef = doc(db, getLogsCollectionName(), guestLogId);
      const updateEvent = {
        timestamp: Timestamp.now(),
        method: "Guest Removed",
        type: "Guest",
        status: "removed",
        isVerified: false,
        verifier: getStaffVerifierTag("Removed")
      };

      await updateDoc(logRef, {
        status: 'removed',
        removedAt: Timestamp.now(),
        scans: arrayUnion(updateEvent)
      });

      setRecentLogs(prev => prev.map(item => item.id === guestLogId ? { ...item, status: 'removed' } : item));
      setGuestItems(prev => prev.filter(g => g.id !== guestLogId));
      toast.info("Guest marked as removed");
    } catch (err) {
      console.error("Error removing guest:", err);
      toast.error("Failed to mark guest as removed");
    }
  };

  const handleManualSubmit = async () => {
    const input = manualInput.trim();
    if (!input) return toast.error("Enter name/email");
    try {
      const member = await verifyMemberProfile(input);

      if (member && member.isBanned) {
        const t = await logVisit(member.name, "Manual", "Guest", input, "", false, false, false, true);
        setModalContent({
          title: "Access Denied", 
          name: member.name, 
          email: input, 
          time: t, 
          type: "Guest", 
          variant: 'danger', 
          isBanned: true, 
          subtext: `BANNED: ${member.banReason}`
        });
        setManualInput('');
        setManualMode(false);
        setShowModal(true);
        return;
      }

      if (!member || !member.isMember) {
        toast.error("Member profile not found or user is not an active member");
        return;
      }

      const isDenied = member.expired || member.flagged || member.incident || member.isBanned;
      const resolvedEmail = member.email || (input.includes('@') ? input : "");

      if (!isDenied && resolvedEmail !== "") {
        setLastMemberName(member.name);
        setLastMemberEmail(resolvedEmail.toLowerCase());
        fetchGuestsForMember(resolvedEmail.toLowerCase());
      }

      const t = await logVisit(member.name, "Manual", "Member", resolvedEmail, member.name, member.expired, member.flagged, member.incident, member.isBanned);

      setModalContent({
        title: isDenied ? "Access Denied" : "Check-in Successful", 
        name: member.name, 
        email: resolvedEmail, 
        time: t, 
        type: "Member",
        variant: isDenied ? 'danger' : 'success', 
        isFlagged: member.flagged, 
        hasIncident: member.incident, 
        isBanned: member.isBanned,
        subtext: member.isBanned 
          ? `BANNED: ${member.banReason}` 
          : (member.flagged 
              ? "Flagged Account" 
              : (member.incident 
                  ? "Open Incident" 
                  : (member.expired 
                      ? `Expired: ${member.expiryDate}` 
                      : "")))
      });

      setManualInput('');
      setManualMode(false);
      setShowModal(true);
    } catch (err) {
      console.error("Error in manual submit:", err);
    }
  };

  const handleScan = (result) => {
    if (result && result.length > 0 && !scannedResult) {
      const raw = result[0].rawValue; setScannedResult(raw); setIsPaused(true);
      verifyMemberProfile(raw).then(member => {
        if (member && member.isBanned) {
          logVisit(member.name, "QR Scan", "Member", member.email || raw, member.name, member.expired, member.flagged, member.incident, true).then(t => {
            setModalContent({ 
              title: "Access Denied", 
              name: member.name, 
              variant: 'danger', 
              isBanned: true, 
              subtext: `BANNED: ${member.banReason}`,
              type: "Member",
              email: member.email || raw
            });
            setShowModal(true);
          });
        } else if (member && member.isMember) {
          logVisit(member.name, "QR Scan", "Member", member.email || raw, member.name, member.expired, member.flagged, member.incident, member.isBanned).then(t => {
            const isDenied = member.expired || member.flagged || member.incident || member.isBanned;
            if (!isDenied && (member.email || raw).includes('@')) {
              setLastMemberName(member.name); setLastMemberEmail((member.email || raw).toLowerCase());
              fetchGuestsForMember((member.email || raw).toLowerCase());
            }
            setModalContent({ 
              title: isDenied ? "Access Denied" : "Check-in Successful", 
              name: member.name, 
              variant: isDenied ? 'danger' : 'success',
              isFlagged: member.flagged,
              hasIncident: member.incident,
              isBanned: member.isBanned,
              subtext: member.isBanned 
                ? `BANNED: ${member.banReason}` 
                : (member.flagged 
                    ? "Flagged Account" 
                    : (member.incident 
                        ? "Open Incident" 
                        : (member.expired 
                            ? `Expired: ${member.expiryDate}` 
                            : ""))),
              type: "Member",
              email: member.email || raw
            });
            setShowModal(true);
          });
        } else {
          setModalContent({ 
            title: "Access Denied", 
            name: "Invalid Member Profile", 
            variant: 'danger', 
            isBanned: false,
            subtext: "Profile not found or inactive."
          });
          setShowModal(true);
        }
      });
    }
  };

  const filteredLogsList = useMemo(() => {
    const searchLower = deferredSearchTerm.trim().toLowerCase();
    return recentLogs.filter(log => {
      const isRemovedOrNoShow = log.status === 'removed' || log.status === 'cancelled' || log.noshow === true || log.remove === true;
      const isDeniedOrFlagged = log.isBanned === true || log.isExpired === true || log.isFlagged === true || log.hasIncident === true || log.status === 'denied' || log.status === 'blocked';
      
      const shouldBeInAudit = isRemovedOrNoShow || isDeniedOrFlagged;

      if (quickViewMode === 'removed') {
        if (!shouldBeInAudit) return false;
      } 
      else if (quickViewMode === 'unverified') {
        if (shouldBeInAudit || log.isVerified) return false;
      } 
      else {
        if (shouldBeInAudit || !log.isVerified) return false;

        if (quickViewMode === 'members' && log.entryType === 'Guest') return false;
        if (quickViewMode === 'guests' && log.entryType !== 'Guest') return false;
      }

      if (!searchLower) return true;

      const nameMatch = log.fullName && log.fullName.toLowerCase().includes(searchLower);
      const sponsorMatch = log.sponsorName && log.sponsorName.toLowerCase().includes(searchLower);
      const signedInByMatch = log.signedInBy && log.signedInBy.toLowerCase().includes(searchLower);
      const rawPayloadMatch = log.rawPayload && log.rawPayload.toLowerCase().includes(searchLower);

      return nameMatch || sponsorMatch || signedInByMatch || rawPayloadMatch;
    });
  }, [recentLogs, deferredSearchTerm, quickViewMode]);

  const visibleLogs = useMemo(() => {
    if (deferredSearchTerm.trim().length > 0) return filteredLogsList;
    return filteredLogsList.slice(0, visibleCount);
  }, [filteredLogsList, visibleCount, deferredSearchTerm]);

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 20);
  };

  const hasMoreLogs = deferredSearchTerm.trim().length === 0 && filteredLogsList.length > visibleLogs.length;

  return (
    <div className="qr-scanner-page">
      {!isAdminAuth ? (
        <div className="p-4 qr-modal-content shadow-lg mt-5 mx-auto" style={{ maxWidth: '420px' }}>
          <Form.Control type="password" placeholder="Passcode" value={passcodeAttempt} onChange={(e) => setPasscodeAttempt(e.target.value)} className="qr-input mb-4 text-center" />
          <button className="qr-btn qr-btn-primary w-100" onClick={() => passcodeAttempt === ADMIN_PASSCODE ? setIsAdminAuth(true) : toast.error("Invalid Passcode")}>UNLOCK STATION</button>
        </div>
      ) : (
        <>
          {!manualMode ? (
            <>
              <div className="qr-video-container mb-3 border border-secondary rounded overflow-hidden shadow-sm pos-relative outer-scanner-frame">
                <CameraScanner onScan={handleScan} isPaused={effectiveIsPaused} />
              </div>

              <Row className="g-2 mb-4 align-items-center">
                <Col xs={8}><button className="qr-btn qr-btn-warning w-100" onClick={() => setManualMode(true)}>Manual Sign In</button></Col>
                <Col xs={2} className="d-flex justify-content-center">
                  <button onClick={() => setShowHistoryModal(true)} className="qr-icon-btn" title="Filter Date Logs">
                    <FaFilter size={18} />
                  </button>
                </Col>
                <Col xs={2} className="d-flex justify-content-end"><button className="qr-icon-btn" style={{ color: 'var(--brand-danger)' }} onClick={() => setIsAdminAuth(false)}>🔒</button></Col>
              </Row>

              <InputGroup className="mb-2 shadow-sm">
                <Form.Control placeholder="🔎 Filter names or sponsors..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="glass-search" />
              </InputGroup>

              <div className="d-flex flex-wrap gap-1 mb-2 justify-content-start">
                {/* SHIFT TOGGLE BUTTON */}
                <button 
                  className={`btn btn-sm flex-fill font-11 fw-bold ${isShiftView ? 'btn-warning text-dark' : 'btn-outline-warning text-warning'}`} 
                  onClick={() => { 
                    const nextShiftState = !isShiftView;
                    setIsShiftView(nextShiftState); 
                    setQuickViewMode('all'); 
                    setVisibleCount(INITIAL_DISPLAY_LIMIT);
                    fetchLogs(true, nextShiftState); 
                  }}
                  title="Toggle Active Shift View (4 PM Yesterday to Now)"
                >
                  🌙 Shift {isShiftView ? '(On)' : ''}
                </button>

                <button className={`btn btn-sm flex-fill font-11 ${quickViewMode === 'all' && !isShiftView ? 'btn-info text-white fw-bold' : 'btn-outline-secondary text-muted'}`} onClick={() => { setIsShiftView(false); setQuickViewMode('all'); setVisibleCount(INITIAL_DISPLAY_LIMIT); fetchLogs(true, false); }}>All</button>
                <button className={`btn btn-sm flex-fill font-11 ${quickViewMode === 'members' ? 'btn-info text-white fw-bold' : 'btn-outline-secondary text-muted'}`} onClick={() => { setQuickViewMode('members'); setVisibleCount(INITIAL_DISPLAY_LIMIT); }}>Members</button>
                <button className={`btn btn-sm flex-fill font-11 ${quickViewMode === 'guests' ? 'btn-info text-white fw-bold' : 'btn-outline-secondary text-muted'}`} onClick={() => { setQuickViewMode('guests'); setVisibleCount(INITIAL_DISPLAY_LIMIT); }}>Guests</button>
                
                <button 
                  className={`btn btn-sm flex-fill font-11 d-flex align-items-center justify-content-center ${quickViewMode === 'unverified' ? 'btn-info text-white fw-bold' : 'btn-outline-secondary text-muted'}`} 
                  onClick={() => { setQuickViewMode('unverified'); setVisibleCount(INITIAL_DISPLAY_LIMIT); }}
                  title="View Unverified Cards Needing Action"
                >
                  <FaUserCheck className="me-1" size={11} /> Verify
                </button>

                <button 
                  className={`btn btn-sm flex-fill font-11 d-flex align-items-center justify-content-center ${quickViewMode === 'removed' ? 'btn-info text-white fw-bold' : 'btn-outline-secondary text-muted'}`} 
                  onClick={() => { setQuickViewMode('removed'); setVisibleCount(INITIAL_DISPLAY_LIMIT); }}
                  title="View Removed / Denied / Banned Entries"
                >
                  <FaInfoCircle className="me-1" size={11} /> Audit
                </button>

                <button className="btn btn-sm flex-fill font-11 btn-outline-info text-info fw-bold" onClick={() => setShowEventListModal(true)}>
                  <FaClipboardList className="me-1" size={12} /> Event List
                </button>
              </div>

              <div className="d-flex justify-content-between align-items-center px-1 mb-2">
                <span className="qr-subtext-small text-white-50" style={{ fontSize: '0.72rem' }}>
                  Showing <strong className="text-white">{visibleLogs.length}</strong> of <strong className="text-info">{filteredLogsList.length}</strong> Total Logs
                </span>

                {hasMoreLogs && (
                  <button 
                    className="btn btn-xs btn-primary text-white py-0.5 px-2.5 d-inline-flex align-items-center gap-1 shadow-sm rounded-1"
                    style={{ fontSize: '0.68rem', lineHeight: '1.2' }}
                    onClick={handleLoadMore}
                  >
                    <FaChevronDown size={9} /> Load More
                  </button>
                )}
              </div>

              <div className="qr-card-list">
                {visibleLogs.map((log) => (
                  <LogCardItem
                    key={log.id} log={log} formatDisplayValue={formatDisplayValue} formatScanTimeOnly={formatScanTimeOnly}
                    handleNoShow={handleNoShow} handleVerifyEntry={handleVerifyEntry} handleOpenBanModal={handleOpenBanModal}
                    setSelectedLog={setSelectedLog} setShowDetailModal={setShowDetailModal} setLastMemberEmail={setLastMemberEmail}
                    setLastMemberName={setLastMemberName} fetchGuestsForMember={fetchGuestsForMember} setShowGuestModal={setShowGuestModal} db={db}
                  />
                ))}

                {hasMoreLogs && (
                  <div className="text-center my-3">
                    <button 
                      className="qr-btn qr-btn-primary py-2 px-4 d-inline-flex align-items-center gap-2 shadow-sm"
                      style={{ fontSize: '0.82rem' }}
                      onClick={handleLoadMore}
                    >
                      <FaChevronDown size={12} /> LOAD MORE ({filteredLogsList.length - visibleLogs.length} REMAINING)
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="qr-table-container shadow-lg mt-3 p-4">
              <h4 className="text-white fw-bold m-0 text-uppercase">Manual Sign In</h4>
              <Form.Group className="mb-3 text-start mt-3">
                <label className="qr-label">MEMBER NAME OR EMAIL</label>
                <Form.Control type="text" value={manualInput} onChange={(e) => setManualInput(e.target.value)} className="glass-input" />
              </Form.Group>
              <button className="qr-btn qr-btn-primary w-100" onClick={handleManualSubmit}>VERIFY MEMBER</button>
              <button className="qr-btn qr-btn-warning w-100 mt-2" onClick={() => { setManualInput(''); setManualMode(false); }}>RETURN</button>
            </div>
          )}

          {/* HISTORICAL FILTER MODAL */}
          <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} centered contentClassName="qr-modal-content" backdropClassName='bg-dark'>
            <div className="qr-modal-success-bar" style={{ background: 'var(--brand-info, #0dcaf0)' }}></div>
            <Modal.Header closeButton className="border-secondary text-white">
              <Modal.Title className="fw-bold text-uppercase" style={{ fontSize: '1rem' }}>
                <FaFilter className="me-2" /> Filter Historical Logs
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4 text-white text-start">
              <Row className="g-3 mb-3">
                <Col xs={12} md={6}>
                  <label className="qr-label mb-1">START DATE</label>
                  <Form.Control type="date" size="sm" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="qr-input" />
                </Col>
                <Col xs={12} md={6}>
                  <label className="qr-label mb-1">END DATE</label>
                  <Form.Control type="date" size="sm" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="qr-input" />
                </Col>
              </Row>

              <button 
                className="qr-btn qr-btn-primary w-100 py-2 mb-2 d-flex align-items-center justify-content-center" 
                onClick={() => { fetchLogs(false, false); setShowHistoryModal(false); }}
                disabled={loadingLogs}
              >
                {loadingLogs ? <Spinner size="sm" className="me-2" /> : null}
                LOAD HISTORICAL LOGS
              </button>
              <button 
                className="qr-btn qr-btn-warning w-100 py-2 d-flex align-items-center justify-content-center" 
                onClick={downloadCSVByDate} 
                disabled={exportingCSV}
              >
                {exportingCSV ? <Spinner size="sm" className="me-2" /> : <FaDownload className="me-2" />} EXPORT CSV
              </button>
            </Modal.Body>
          </Modal>

          {/* SCAN RESULT MODAL */}
          <Modal show={showModal} onHide={() => { setShowModal(false); setIsPaused(false); setScannedResult(''); }} centered contentClassName="qr-modal-content" backdropClassName='bg-dark'>
            <div className="qr-modal-success-bar" style={{ background: modalContent.variant === 'danger' ? 'var(--brand-secondary)' : 'var(--brand-primary)' }}></div>
            <Modal.Body className="p-4 text-center text-white">
              <div className="mb-2" style={{ fontSize: '3.5rem', color: modalContent.variant === 'danger' ? 'var(--brand-secondary)' : 'var(--brand-primary)' }}>
                {modalContent.variant === 'danger' ? <FaTimesCircle /> : <FaCheckCircle />}
              </div>
              <h2 className="mb-1 fw-bold">{formatDisplayValue(modalContent.name)}</h2>
              
              <div className={`qr-badge mb-3 d-inline-block px-4 py-2 ${modalContent.variant === 'danger' ? 'qr-badge-expired' : 'qr-badge-member'}`}>
                {modalContent.isBanned 
                  ? 'BANNED' 
                  : (modalContent.isFlagged 
                      ? 'FLAGGED' 
                      : (modalContent.hasIncident 
                          ? 'INCIDENT' 
                          : (modalContent.variant === 'danger' 
                              ? 'EXPIRED' 
                              : modalContent.type)))}
              </div>

              {modalContent.subtext && (
                <div className="p-3 mb-3 rounded qr-subtext-warning" style={{ background: 'rgba(255, 193, 7, 0.1)', border: '1px solid rgba(255, 193, 7, 0.3)' }}>
                  <h6 className="m-0 small fw-bold text-warning">{modalContent.subtext}</h6>
                </div>
              )}

              {modalContent.variant === 'danger' && (
                <div className="mt-3 p-3 rounded" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <p className="small text-white-50 mb-2">Security override required?</p>
                  <button className="qr-btn qr-btn-primary w-100 py-2" onClick={handleBypass}>AUTHORIZE BYPASS</button>
                </div>
              )}

              <button className="qr-btn qr-btn-warning w-100 mt-3" onClick={() => { setShowModal(false); setIsPaused(false); setScannedResult(''); }}>Finish</button>
            </Modal.Body>
          </Modal>

          {/* DETAIL MODAL */}
          <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered contentClassName="qr-modal-content" backdropClassName='bg-dark'>
            <div className="qr-modal-success-bar" style={{ background: (selectedLog?.isExpired || selectedLog?.isFlagged || selectedLog?.hasIncident || selectedLog?.isBanned) ? 'var(--brand-secondary)' : 'var(--brand-info)' }}></div>
            <Modal.Body className="p-4 text-white">
              {selectedLog && (
                <div className="text-start">
                  <h3 className="fw-bold mb-2 text-center">{formatDisplayValue(selectedLog.fullName)}</h3>
                  {selectedLog.visitCount > 1 && (
                    <div className="text-center mb-4">
                      <span className="badge bg-info text-dark font-11">Total Visits Today: {selectedLog.visitCount}</span>
                    </div>
                  )}
                  <Row className="mb-2">
                    <Col xs={5} className="small text-uppercase fw-bold opacity-50">Status:</Col>
                    <Col className={`small fw-bold ${(selectedLog.isExpired || selectedLog.isFlagged || selectedLog.hasIncident || selectedLog.isBanned) ? "text-danger" : "text-success"}`}>
                      {selectedLog.isBanned ? "BANNED" : selectedLog.isFlagged ? "FLAGGED" : selectedLog.hasIncident ? "INCIDENT" : selectedLog.isExpired ? "EXPIRED" : "SUCCESS"}
                    </Col>
                  </Row>
                  {selectedLog.bypassReason && (
                    <Row className="mb-2">
                      <Col xs={5} className="small text-uppercase fw-bold opacity-50">Reason:</Col>
                      <Col className="small text-warning italic">{selectedLog.bypassReason}</Col>
                    </Row>
                  )}
                  <Row className="mb-2">
                    <Col xs={5} className="small text-uppercase fw-bold opacity-50">Last Scan:</Col>
                    <Col className="small">{formatScanTime(selectedLog.lastScannedAt || selectedLog.scannedAt)}</Col>
                  </Row>

                  {selectedLog.scans && selectedLog.scans.length > 0 && (
                    <div className="mt-3 pt-3 border-top border-secondary">
                      <label className="qr-label mb-2 d-block text-white fw-bold">
                        Shift Scan Timeline ({selectedLog.scans.length}):
                      </label>
                      <div className="custom-scroll" style={{ maxHeight: '140px', overflowY: 'auto' }}>
                        {selectedLog.scans.map((s, idx) => (
                          <div key={idx} className="small text-light d-flex justify-content-between align-items-center py-1 px-2 border-bottom border-secondary" style={{ background: 'rgba(255,255,255,0.05)' }}>
                            <span className="fw-semibold text-info">{s.method || s.type || 'Check-in'}</span>
                            <span className="text-white-50">{formatScanTimeOnly(s.timestamp)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <button className="qr-btn qr-btn-warning w-100 mt-3" onClick={() => setShowDetailModal(false)}>Close</button>
            </Modal.Body>
          </Modal>

          {/* GUEST MANAGEMENT MODAL */}
          <Modal show={showGuestModal} onHide={() => setShowGuestModal(false)} centered contentClassName="qr-modal-content bg-dark-profile" backdropClassName='bg-dark'>
            <div className="qr-modal-success-bar" style={{ background: 'var(--brand-primary)' }}></div>
            <Modal.Body className="p-4 text-white">
              <h2 className="fw-bold mb-1 text-center">Guest Management</h2>
              <p className="qr-subtext small text-center mb-4 opacity-50">Sponsor: {formatDisplayValue(lastMemberName)}</p>

              <InputGroup className="mb-4">
                <Form.Control 
                  placeholder="Guest Name..." 
                  className="qr-input" 
                  value={guestNameInput} 
                  onChange={(e) => setGuestNameInput(e.target.value)} 
                  onKeyPress={(e) => e.key === 'Enter' && handleQuickGuestAdd()} 
                />
                <button className="qr-btn qr-btn-primary px-3" onClick={handleQuickGuestAdd}>
                  ADD
                </button>
              </InputGroup>

              <div className="qr-card-list custom-scroll" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                {guestItems.map((guest) => (
                  <div key={guest.id} className="qr-log-card py-2 px-3 d-flex justify-content-between align-items-center mb-2" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <div className="d-flex align-items-center gap-2">
                      {guest.isVerified ? (
                        <FaCheckCircle className="text-success" title={`Verified by ${guest.verifiedBy || 'Staff'}`} size={14} />
                      ) : (
                        <FaTimesCircle className="text-danger" title="Unverified Guest" size={14} />
                      )}
                      <span className="fw-bold small text-white">{formatDisplayValue(guest.fullName)}</span>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      {!guest.isVerified && (
                        <button
                          className="btn btn-sm text-success fw-bold border-0 bg-transparent p-0 me-2"
                          onClick={() => handleVerifyEntry(guest)}
                        >
                          VERIFY
                        </button>
                      )}
                      <button
                        className="btn btn-sm text-danger fw-bold border-0 bg-transparent p-0"
                        onClick={() => {
                          if (window.confirm(`Remove ${guest.fullName} from active view?`)) {
                            handleRemoveGuest(guest.id);
                          }
                        }}
                      >
                        REMOVE
                      </button>
                    </div>
                  </div>
                ))}
                {guestItems.length === 0 && <div className="text-center py-4 qr-subtext opacity-50">No active guests added yet</div>}
              </div>

              <button className="qr-btn qr-btn-warning w-100 mt-4" onClick={() => { setShowGuestModal(false); fetchLogs(true, isShiftView); }}>
                Close
              </button>
            </Modal.Body>
          </Modal>

          {/* BAN DOCUMENT MODAL */}
          <Modal show={showBanModal} onHide={() => setShowBanModal(false)} centered contentClassName="qr-modal-content" backdropClassName='bg-dark'>
            <div className="qr-modal-success-bar" style={{ background: 'var(--brand-secondary, #dc3545)' }}></div>
            <Modal.Body className="p-4 text-white">
              <div className="text-center mb-3">
                <FaBan size={36} className="text-danger mb-2" />
                <h4 className="fw-bold m-0 text-uppercase">Create Ban Document</h4>
              </div>
              <Form.Group className="mb-2 text-start">
                <Form.Label className="qr-label mb-1">FIRST NAME</Form.Label>
                <Form.Control type="text" className="qr-input" value={banForm.firstName} onChange={(e) => setBanForm({ ...banForm, firstName: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-2 text-start">
                <Form.Label className="qr-label mb-1">LAST NAME</Form.Label>
                <Form.Control type="text" className="qr-input" value={banForm.lastName} onChange={(e) => setBanForm({ ...banForm, lastName: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3 text-start">
                <Form.Label className="qr-label mb-1">REASON</Form.Label>
                <Form.Control as="textarea" rows={2} className="qr-input" value={banForm.reason} onChange={(e) => setBanForm({ ...banForm, reason: e.target.value })} />
              </Form.Group>
              <button className="qr-btn qr-btn-primary w-100 mb-2" style={{ background: 'var(--brand-secondary, #dc3545)' }} onClick={handleSaveBan} disabled={savingBan}>
                {savingBan ? <Spinner size="sm" /> : "COMMIT TO GLOBAL BAN LIST"}
              </button>
              <button className="qr-btn qr-btn-warning w-100" onClick={() => setShowBanModal(false)}>Cancel</button>
            </Modal.Body>
          </Modal>

          {/* OVERRIDE MODAL */}
          <Modal show={showOverrideModal} onHide={() => setShowOverrideModal(false)} centered contentClassName="qr-modal-content" backdropClassName='bg-dark'>
            <div className="qr-modal-success-bar" style={{ background: 'var(--brand-warning, #ffc107)' }}></div>
            <Modal.Body className="p-4 text-white text-start">
              <div className="text-center mb-3">
                <FaExclamationTriangle size={36} className="text-warning mb-2" />
                <h4 className="fw-bold m-0 text-uppercase">Banned List Name Match</h4>
              </div>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="diff-person-chk"
                  label={<span className="small text-white fw-bold">Different person with same name</span>}
                  checked={isDifferentPerson}
                  onChange={(e) => setIsDifferentPerson(e.target.checked)}
                />
              </Form.Group>
              {isDifferentPerson && (
                <>
                  <Form.Group className="mb-2">
                    <Form.Label className="qr-label mb-1">ADMIN PASSCODE</Form.Label>
                    <Form.Control type="password" className="qr-input" value={overridePasscode} onChange={(e) => setOverridePasscode(e.target.value)} />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="qr-label mb-1">NOTES</Form.Label>
                    <Form.Control as="textarea" rows={2} className="qr-input" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
                  </Form.Group>
                </>
              )}
              <button className="qr-btn qr-btn-primary w-100 mb-2" onClick={handleConfirmOverride}>
                <FaShieldAlt className="me-2" />
                {isDifferentPerson ? "COMMIT OVERRIDE & VERIFY" : "CONFIRM BAN & BLOCK"}
              </button>
              <button className="qr-btn qr-btn-warning w-100" onClick={() => setShowOverrideModal(false)}>Cancel</button>
            </Modal.Body>
          </Modal>

          {/* EVENT LIST MODAL COMPONENT */}
          <EventListManager
            show={showEventListModal}
            onHide={() => setShowEventListModal(false)}
            isAdmin={isAdmin}
            isBarAdmin={isBarAdmin}
            db={db}
            formatDisplayValue={formatDisplayValue}
            logVisit={logVisit}
            verifyMemberProfile={verifyMemberProfile}
            getStaffVerifierTag={getStaffVerifierTag}
            onCheckInSuccess={() => fetchLogs(true, isShiftView)}
          />
        </>
      )}
    </div>
  );
};

export default QRWebAndNativeScan;